import { spacing, typography } from "../app/design-system";
import { DownloadOutlined,FileTextOutlined } from "@ant-design/icons";
import { Button,Typography } from "../../../../../shared/frontend/ui";
import React from "react";
import type { ApiClient } from "../app/api/client";
import { ApiError } from "../app/api/client";
import { HTTP_STATUS_NOT_FOUND } from "../app/constants/httpStatus";
import { DocumentViewer } from "../../../../../shared/frontend/components/DocumentViewer";
import { renderDocumentViewerLoading } from "./renderDocumentViewerLoading";

const MIME_TEXT_HTML = "text/html";

const DOCUMENT_CONTENT_TYPE_PDF = "application/pdf";
const DOCUMENT_CONTENT_TYPE_WORD_OPEN_XML = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
const DOCUMENT_CONTENT_TYPE_WORD_BINARY = "application/msword";
const DOCUMENT_PDF_HEADER_BYTES = [0x25, 0x50, 0x44, 0x46] as const;
const DOCUMENT_WORD_EXTENSION_PATTERN = /\.(docx?|docm|dotx?|dotm)$/i;
const DOCUMENT_IMAGE_EXTENSION_PATTERN = /\.(jpe?g|png|gif|webp|bmp)$/i;

type DocumentBlobPreviewContentProps = {
  api: ApiClient;
  previewUrl: string;
  fileName: string;
  title?: string | null;
  contentType?: string | null;
  downloadUrl?: string;
  text: (key: string) => string;
  /** Schlüssel, unter dem der Viewer die Zoomwahl je Gerät merkt. */
  zoomStorageKey?: string;
};

function normalizeDocumentContentType(contentType: string | undefined | null) {
  return (contentType || "").toLowerCase().split(";")[0].trim();
}

function isAmbiguousDocumentContentType(contentType: string) {
  return !contentType || (contentType.includes("octet") && contentType.includes("stream"));
}

function isDocumentPdf(fileName: string, contentType: string) {
  return contentType === DOCUMENT_CONTENT_TYPE_PDF || fileName.toLowerCase().endsWith(".pdf");
}

function isDocumentWord(fileName: string, contentType: string) {
  return contentType === DOCUMENT_CONTENT_TYPE_WORD_OPEN_XML
    || contentType === DOCUMENT_CONTENT_TYPE_WORD_BINARY
    || DOCUMENT_WORD_EXTENSION_PATTERN.test(fileName);
}

function isDocumentImage(fileName: string, contentType: string) {
  return contentType.startsWith("image/") || DOCUMENT_IMAGE_EXTENSION_PATTERN.test(fileName);
}

function hasDocumentPdfHeader(data: Uint8Array) {
  return DOCUMENT_PDF_HEADER_BYTES.every((byte, index) => data[index] === byte);
}

export function DocumentBlobPreviewContent({
  api,
  previewUrl,
  fileName,
  title,
  contentType,
  downloadUrl,
  text,
  zoomStorageKey
}: DocumentBlobPreviewContentProps) {
  const [blobUrl, setBlobUrl] = React.useState<string | null>(null);
  const [pdfData, setPdfData] = React.useState<Uint8Array | null>(null);
  const [textContent, setTextContent] = React.useState<string | null>(null);
  const [previewKind, setPreviewKind] = React.useState<"pdf" | "image" | "text" | "html" | null>(null);
  const [previewError, setPreviewError] = React.useState<"notFound" | "loadFailed" | null>(null);
  const [loading, setLoading] = React.useState(true);
  const blobUrlRef = React.useRef<string | null>(null);

  const ct = normalizeDocumentContentType(contentType);
  const fn = (fileName || "").toLowerCase();
  const isPdf = isDocumentPdf(fn, ct);
  const isWord = isDocumentWord(fn, ct);
  const isImage = isDocumentImage(fn, ct);
  const isText = ct.startsWith("text/");
  const isAmbiguous = isAmbiguousDocumentContentType(ct);
  const canAttemptInlinePreview = isPdf || isWord || isImage || isText || isAmbiguous;
  const displayTitle = title || fileName;

  React.useEffect(() => {
    let cancelled = false;

    const revokeBlobUrl = () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };

    revokeBlobUrl();
    setBlobUrl(null);
    setPdfData(null);
    setTextContent(null);
    setPreviewKind(null);
    setPreviewError(null);

    if (!previewUrl || !canAttemptInlinePreview) {
      setLoading(false);
      return () => {
        cancelled = true;
        revokeBlobUrl();
      };
    }

    setLoading(true);
    api.blob(previewUrl)
      .then(async (blob) => {
        if (cancelled) return;

        const blobCt = normalizeDocumentContentType(blob.type);
        if (isPdf || isWord || isAmbiguous || blobCt === DOCUMENT_CONTENT_TYPE_PDF) {
          const data = new Uint8Array(await blob.arrayBuffer());
          if (cancelled) return;
          if (isPdf || blobCt === DOCUMENT_CONTENT_TYPE_PDF || hasDocumentPdfHeader(data)) {
            setPreviewKind("pdf");
            setPdfData(data);
            return;
          }
        }

        if (isImage || blobCt.startsWith("image/")) {
          const url = URL.createObjectURL(blob);
          if (cancelled) {
            URL.revokeObjectURL(url);
            return;
          }
          blobUrlRef.current = url;
          setPreviewKind("image");
          setBlobUrl(url);
          return;
        }

        if (isText || blobCt.startsWith("text/")) {
          const content = await blob.text();
          if (cancelled) return;
          setTextContent(content);
          setPreviewKind(blobCt === MIME_TEXT_HTML ? "html" : "text");
          return;
        }

        setBlobUrl(null);
        setPdfData(null);
        setPreviewKind(null);
      })
      .catch((error) => {
        if (!cancelled) {
          setBlobUrl(null);
          setPdfData(null);
          setTextContent(null);
          setPreviewKind(null);
          setPreviewError(error instanceof ApiError && error.status === HTTP_STATUS_NOT_FOUND ? "notFound" : "loadFailed");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      revokeBlobUrl();
    };
  }, [
    api,
    canAttemptInlinePreview,
    isAmbiguous,
    isImage,
    isPdf,
    isText,
    isWord,
    previewUrl
  ]);

  if (canAttemptInlinePreview && loading) return renderDocumentViewerLoading();

  const viewerProps = {
    title: displayTitle,
    zoomStorageKey,
    translate: text,
    loadingText: text("Loading…"),
    renderLoading: renderDocumentViewerLoading
  };

  if (previewKind === "pdf" && pdfData) {
    return <DocumentViewer {...viewerProps} kind="pdf" fileData={pdfData} errorText={text("PDF preview could not be loaded.")} />;
  }

  if (previewKind === "image" && blobUrl) {
    return <DocumentViewer {...viewerProps} kind="image" imageSrc={blobUrl} errorText={text("Document preview could not be loaded.")} />;
  }

  if ((previewKind === "text" || previewKind === "html") && textContent != null) {
    return previewKind === "html"
      ? <DocumentViewer {...viewerProps} kind="html" html={textContent} keepDocumentColors errorText={text("Document preview could not be loaded.")} />
      : <DocumentViewer {...viewerProps} kind="text" text={textContent} errorText={text("Document preview could not be loaded.")} />;
  }

  if (previewError) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", minHeight: 360, padding: spacing.previewPadding }}>
        <Typography.Text type="secondary">
          {text(previewError === "notFound" ? "Document file not found." : "Document preview could not be loaded.")}
        </Typography.Text>
      </div>
    );
  }

  if (canAttemptInlinePreview) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", minHeight: 360, padding: spacing.previewPadding }}>
        <Typography.Text type="secondary">{text("No preview available for this file type.")}</Typography.Text>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", height: "100%", minHeight: 360, padding: spacing.previewPadding }}>
      <FileTextOutlined style={{ fontSize: typography.fontSize.hero, color: "#999", marginBottom: spacing.xl }} />
      <Typography.Text type="secondary">
        {text("No preview available for this file type.")}
      </Typography.Text>
      <Typography.Text type="secondary" style={{ marginTop: spacing.sm }}>
        {contentType}
      </Typography.Text>
      <Button
        type="primary"
        icon={<DownloadOutlined />}
        disabled={!downloadUrl}
        style={{ marginTop: spacing.xl }}
        onClick={() => {
          if (downloadUrl) window.open(downloadUrl, "_blank", "noopener,noreferrer");
        }}
      >
        {text("Download document")}
      </Button>
    </div>
  );
}
